<template>
  <header
    class="nav"
    :class="{ 'is-scrolled': isSolid, 'is-open': isMenuOpen }"
  >
    <div class="nav__inner">
      <button
        class="nav__burger"
        aria-label="Menu"
        @click="isMenuOpen = !isMenuOpen"
      >
        <span /><span /><span />
      </button>

      <nav class="nav__links nav__links--left">
        <a
          v-for="item in leftItems"
          :key="item.anchor"
          :href="item.anchor"
          @click.prevent="goTo(item.anchor)"
        >{{ item.label }}</a>
      </nav>

      <a
        class="nav__logo"
        href="/"
        @click.prevent="goTo('#top')"
      >
        <img
          :src="data.site.logo"
          alt="Templiers Valley"
        >
      </a>

      <nav class="nav__links nav__links--right">
        <a
          v-for="item in rightItems"
          :key="item.anchor"
          :href="item.anchor"
          @click.prevent="goTo(item.anchor)"
        >{{ item.label }}</a>
        <button
          class="btn-pill is-red nav__contact"
          @click="$emit('open-contact')"
        >
          <BtnStar />
          {{ data.nav.contactLabel }}
        </button>
      </nav>
    </div>

    <transition name="menu">
      <nav
        v-if="isMenuOpen"
        class="nav__mobile"
      >
        <a
          v-for="item in data.nav.items"
          :key="item.anchor"
          :href="item.anchor"
          @click.prevent="goTo(item.anchor)"
        >{{ item.label }}</a>
        <button
          class="btn-pill is-red"
          @click="openContactMobile"
        >
          {{ data.nav.contactLabel }}
        </button>
      </nav>
    </transition>
  </header>
</template>

<script>
import BtnStar from '@/components/BtnStar.vue'

export default {
  name: 'SiteNav',
  components: { BtnStar },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  emits: ['open-contact'],
  data() {
    return {
      isScrolled: false,
      isMenuOpen: false
    }
  },
  computed: {
    // fond navy dès qu'on n'est pas sur le hero de la home
    isSolid() {
      return this.isScrolled || this.$route.path !== '/'
    },
    leftItems() {
      return this.data.nav.items.slice(0, 3)
    },
    rightItems() {
      return this.data.nav.items.slice(3)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 40
    },
    goTo(anchor) {
      this.isMenuOpen = false
      if (anchor === '#top' || this.$route.path !== '/') {
        this.$router.push({ path: '/', hash: anchor === '#top' ? '' : anchor })
        if (anchor === '#top') window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      const el = document.querySelector(anchor)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    openContactMobile() {
      this.isMenuOpen = false
      this.$emit('open-contact')
    }
  }
}
</script>

<style lang="scss" scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  padding: 18px 0;
  transition: background 0.3s ease, padding 0.3s ease, box-shadow 0.3s ease;

  &.is-scrolled,
  &.is-open {
    background: $navy;
    padding: 10px 0;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
  }
}

.nav__inner {
  width: min(1280px, calc(100% - 44px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 20px;
}

.nav__links {
  display: flex;
  align-items: center;
  gap: clamp(20px, 3.4vw, 56px);

  a {
    color: $white;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -6px;
      width: 0;
      height: 2px;
      background: $yellow;
      transition: width 0.25s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }

  &--left {
    justify-content: flex-start;
  }

  &--right {
    justify-content: flex-end;
  }
}

.nav__logo {
  img {
    height: 56px;
    width: auto;
  }
}

.nav__contact {
  margin-left: clamp(8px, 1.5vw, 24px);
  padding: 10px 18px;
  font-size: 12px;
}

.nav__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  padding: 8px;

  span {
    display: block;
    height: 2px;
    background: $white;
    border-radius: 2px;
  }
}

.nav__mobile {
  display: none;
  flex-direction: column;
  align-items: flex-start;
  gap: 22px;
  padding: 24px 22px 30px;
  background: $navy;

  a {
    color: $white;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
}

@include tablet-down {
  .nav__mobile {
    display: flex;
  }
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@include tablet-down {
  .nav__inner {
    grid-template-columns: auto 1fr auto;
  }

  .nav__burger {
    display: flex;
  }

  .nav__links {
    display: none;
  }

  .nav__logo {
    justify-self: center;

    img {
      height: 44px;
    }
  }
}
</style>
